import { useState, useEffect } from 'react'
import Select, { type SingleValue } from 'react-select'
import type { ReactNode } from 'react'
import type { ValueMapping } from './CSVEnumSelector.js'
import { useDialogPortal } from '../../../../../../Provider/DialogPortalContext.js'

type MapEnumValuesModalProps<TEnumKeyType extends string, TEnumDTOType extends { key: string; label: string }> = {
  invalidValues: Set<string>
  mapping: Record<string, ValueMapping<TEnumKeyType, TEnumDTOType>>
  handleMappingChange: (changes: Record<string, ValueMapping<TEnumKeyType, TEnumDTOType>>) => void
  enumType: Record<TEnumKeyType, TEnumDTOType>
  close: () => void
}

interface MappingOptionBase {
  value: string
  label: string
  action: string
}

interface DiscardOption extends MappingOptionBase {
  action: 'DISCARD'
}

interface CustomOption extends MappingOptionBase {
  action: 'AS_CUSTOM'
}

interface MapToOption<TEnumKeyType extends string, TEnumDTOType> extends MappingOptionBase {
  action: 'MAP_TO'
  value: TEnumKeyType
  dto: TEnumDTOType
}

type MappingOption<TEnumKeyType extends string, TEnumDTOType> = DiscardOption | CustomOption | MapToOption<TEnumKeyType, TEnumDTOType>

export function MapEnumValuesModal<TEnumKeyType extends string, TEnumDTOType extends { key: string; label: string }>({
  invalidValues,
  mapping,
  handleMappingChange,
  enumType,
  close
}: MapEnumValuesModalProps<TEnumKeyType, TEnumDTOType>) {
  const [bufferedMapping, setBufferedMapping] = useState<typeof mapping>({})
  const { dialogRef } = useDialogPortal()

  // Copy initial mapping into local state
  useEffect(() => {
    setBufferedMapping(mapping)
  }, [mapping])

  const handleBufferedChange = (value: string, mappingType: ValueMapping<TEnumKeyType, TEnumDTOType>) => {
    setBufferedMapping((prev) => ({ ...prev, [value]: mappingType }))
  }

  const applyChanges = () => {
    handleMappingChange(bufferedMapping)
    close()
  }

  function toSelectOption(
    mapping: ValueMapping<TEnumKeyType, TEnumDTOType> | undefined
  ): MappingOption<TEnumKeyType, TEnumDTOType> | undefined {
    if (!mapping) return undefined

    if (mapping.type === 'DISCARD') {
      return { value: 'discard', label: 'Discard this value', action: 'DISCARD' }
    }

    if (mapping.type === 'AS_CUSTOM') {
      return { value: 'custom', label: 'Store as custom value', action: 'AS_CUSTOM' }
    }

    if (mapping.type === 'MAP_TO') {
      return {
        value: mapping.value,
        label: mapping.dto.label,
        action: 'MAP_TO',
        dto: mapping.dto
      }
    }

    return undefined
  }

  const enumEntries = Object.entries(enumType) as [TEnumKeyType, TEnumDTOType][]

  const singleEntry = (
    value: string,
    portal: HTMLElement | null | undefined,
    placeholder?: ReactNode,
    initialValue?: MappingOption<TEnumKeyType, TEnumDTOType>
  ) => {
    return (
      <div key={value} className="mb-2 p-1 border border-gray-200 rounded-lg">
        <div className="font-semibold mb-0.5 text-sm">&quot;{value}&quot;</div>
        <div>
          <Select
            placeholder={placeholder}
            value={initialValue}
            menuPortalTarget={portal}
            onChange={(option: SingleValue<MappingOption<TEnumKeyType, TEnumDTOType>>) => {
              if (!option) return
              if (option.action === 'DISCARD') {
                handleBufferedChange(value, { type: 'DISCARD' })
              } else if (option.action === 'AS_CUSTOM') {
                handleBufferedChange(value, { type: 'AS_CUSTOM' })
              } else if (option.action === 'MAP_TO') {
                handleBufferedChange(value, {
                  type: 'MAP_TO',
                  value: option.value,
                  dto: option.dto
                })
              }
            }}
            options={[
              {
                label: 'General Options',
                options: [
                  {
                    value: 'discard',
                    label: 'Discard this value',
                    action: 'DISCARD'
                  } as DiscardOption
                  /*
                   TODO: this value is disabled until we either decide to enable the custom branch and fully implement or discard the concept
                   {
                    value: 'custom',
                    label: 'Store as custom value',
                    action: 'AS_CUSTOM',
                  } as CustomOption */
                ]
              },
              {
                label: 'Map to existing value',
                options: enumEntries.map(
                  ([key, dto]) =>
                    ({
                      value: key,
                      label: dto.label,
                      action: 'MAP_TO',
                      dto: dto
                    }) as MapToOption<TEnumKeyType, TEnumDTOType>
                )
              }
            ]}
            className="react-select"
            classNamePrefix="select"
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              control: (base, state) => ({
                ...base,
                borderLeft: state.hasValue ? '4px solid #10b981' : '4px solid #facc15',
                borderColor: '#d1d5db',
                borderWidth: '1px',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                minHeight: '2rem',
                boxShadow: 'none'
              }),
              singleValue: (base, state) => ({
                ...base,
                fontStyle: state.hasValue ? 'normal' : 'italic',
                fontWeight: state.hasValue ? 'bold' : 'normal'
              }),
              option: (base) => ({
                ...base,
                whiteSpace: 'normal',
                wordWrap: 'break-word',
                fontSize: '0.875rem'
              }),
              menu: (base) => ({
                ...base,
                zIndex: 1000
              })
            }}
          />
        </div>
      </div>
    )
  }

  return (
    <div>
      <h3 className="font-bold text-lg mb-4">Map Values</h3>
      <p className="mb-4">
        This dialog shows both unmapped values that need mapping and already mapped values. You can map values to existing options, store
        them as custom values, or discard them.
      </p>

      <div className="overflow-y-auto max-h-96">
        {invalidValues.size > 0 && (
          <div>
            <h4 className="font-semibold text-md mb-2">Unmapped Values</h4>
            {Array.from(invalidValues).map((value) =>
              singleEntry(value, dialogRef?.current, 'Select an option', toSelectOption(bufferedMapping[value]))
            )}
          </div>
        )}

        {Object.keys(bufferedMapping).length > 0 && (
          <div className={invalidValues.size > 0 ? 'mt-6 pt-6 border-t border-gray-300' : ''}>
            <h4 className="font-semibold text-md mb-2">Already Mapped Values</h4>
            {Object.entries(bufferedMapping).map(([value, mappingValue]) =>
              singleEntry(
                value,
                dialogRef?.current,
                undefined,
                mappingValue.type === 'DISCARD'
                  ? {
                      value: 'discard',
                      label: 'Discard this value',
                      action: 'DISCARD'
                    }
                  : mappingValue.type === 'AS_CUSTOM'
                    ? {
                        value: 'custom',
                        label: 'Store as custom value',
                        action: 'AS_CUSTOM'
                      }
                    : {
                        value: mappingValue.value,
                        label: mappingValue.dto.label,
                        action: 'MAP_TO',
                        dto: mappingValue.dto
                      }
              )
            )}
          </div>
        )}
      </div>

      <div className="modal-action flex justify-end gap-2 mt-4">
        <button className="btn btn-outline" onClick={close}>
          Cancel
        </button>
        <button className="btn btn-primary" onClick={applyChanges}>
          Apply
        </button>
      </div>
    </div>
  )
}
